import { useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Search, MapPin, ArrowDown } from "lucide-react";
import { KineticLine, EASE } from "./motion";
import { scrollToSection } from "./Navbar";

const INDUSTRIES = ["All Industries", "Technology", "Finance", "Healthcare", "Engineering", "Marketing", "Retail", "Legal", "Consulting"];

export default function Hero({ onSearch }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  const [title, setTitle] = useState("");
  const [location, setLocation] = useState("");
  const [industry, setIndustry] = useState("All Industries");

  const submit = (e) => {
    e.preventDefault();
    onSearch({ q: title, location, industry });
    scrollToSection("jobs");
  };

  return (
    <section id="home" ref={ref} className="relative min-h-screen flex flex-col justify-end overflow-hidden bg-[#0A1128]" data-testid="hero-section">
      <motion.div className="absolute inset-0" style={{ y: bgY }}>
        <img
          src="https://images.pexels.com/photos/1313534/pexels-photo-1313534.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
          alt=""
          className="w-full h-[120%] object-cover opacity-40"
        />
        <div className="absolute inset-0 bg-[#0A1128]/70" />
      </motion.div>

      <motion.div style={{ opacity: fade }} className="relative z-10 px-6 md:px-12 lg:px-24 pt-40 pb-16 w-full">
        <div className="mb-6">
          <KineticLine delay={0.4}>
            <span className="text-xs uppercase tracking-[0.35em] text-white/60">FAM Job Consultancy Pvt. Ltd. — Bengaluru</span>
          </KineticLine>
        </div>

        <h1 className="font-display font-black text-white leading-[0.9] tracking-tighter text-[13vw] sm:text-7xl lg:text-[7.5rem] mb-10" data-testid="hero-headline">
          <KineticLine delay={0.55}>FIND YOUR</KineticLine>
          <KineticLine delay={0.7}>
            <span className="text-[#1D4ED8]">DREAM JOB</span>
          </KineticLine>
          <KineticLine delay={0.85}>
            <span className="inline-flex items-center gap-[0.15em]">
              <span className="text-outline">WITH</span>
              <img src="/fam-logo.png" alt="FAM" className="h-[1.6em] w-auto inline-block drop-shadow-2xl" data-testid="hero-headline-logo" />
            </span>
          </KineticLine>
        </h1>

        <motion.form
          onSubmit={submit}
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 1.1, ease: EASE }}
          className="backdrop-blur-xl bg-white/10 border border-white/20 shadow-[0_8px_32px_rgba(0,0,0,0.3)] p-2 flex flex-col md:flex-row gap-2 max-w-4xl"
          data-testid="hero-search-form"
        >
          <div className="flex items-center gap-3 flex-1 px-4 py-3 border-b md:border-b-0 md:border-r border-white/15">
            <Search size={18} className="text-white/50 shrink-0" />
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Job title or company"
              className="bg-transparent w-full text-white placeholder-white/40 text-sm outline-none"
              data-testid="hero-search-title"
            />
          </div>
          <div className="flex items-center gap-3 flex-1 px-4 py-3 border-b md:border-b-0 md:border-r border-white/15">
            <MapPin size={18} className="text-white/50 shrink-0" />
            <input
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder="Location"
              className="bg-transparent w-full text-white placeholder-white/40 text-sm outline-none"
              data-testid="hero-search-location"
            />
          </div>
          <select
            value={industry}
            onChange={(e) => setIndustry(e.target.value)}
            className="bg-transparent text-white/80 text-sm px-4 py-3 outline-none md:w-44 [&>option]:text-black"
            data-testid="hero-search-industry"
          >
            {INDUSTRIES.map((i) => <option key={i}>{i}</option>)}
          </select>
          <button
            type="submit"
            className="bg-[#1D4ED8] hover:bg-[#1E3A8A] text-white text-xs uppercase tracking-[0.2em] px-10 py-4 transition-colors duration-300"
            data-testid="hero-search-submit"
          >
            Search Jobs
          </button>
        </motion.form>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.6, duration: 1 }}
          className="mt-10 flex flex-wrap gap-x-10 gap-y-3 text-white/50 text-xs uppercase tracking-[0.2em]"
          data-testid="hero-stats-strip"
        >
          <span>12,000+ Placements</span>
          <span>480+ Partner Companies</span>
          <span>15+ Years of Trust</span>
        </motion.div>
      </motion.div>

      <motion.button
        onClick={() => scrollToSection("jobs")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2 }}
        className="absolute bottom-8 right-6 md:right-12 lg:right-24 z-10 text-white/50 hover:text-white transition-colors"
        data-testid="hero-scroll-down"
        aria-label="Scroll down"
      >
        <motion.div animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 2 }}>
          <ArrowDown size={22} />
        </motion.div>
      </motion.button>
    </section>
  );
}
